import type { Address, PublicClient } from "viem"
import { useState } from "react"
import { useParams } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { ShowWhenTrue } from "@/components/conditionals"
import { InnerCard } from "@/components/inner-card"
import { InnerCardWarning } from "@/components/inner-card-warning"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/tooltip"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { ClFarmStore } from "@/domains/cl-farm"
import { AlmProviderImageLink } from "@/domains/cl-farms-page/components/alm-provider-image-link"
import { ClFarmsPageLayout } from "@/domains/cl-farms-page/components/cl-farms-page-layout"
import { ClFarmsSubHeader } from "@/domains/cl-farms-page/components/cl-farms-sub-header"
import { MetaData } from "@/domains/common/layout/metadata"
import { PageContainer } from "@/domains/common/layout/page-container"
import { PageTitle } from "@/domains/common/layout/page-title"
import { DollarIcon, WalletIcon } from "@/domains/icons"
import { stakeClFarmLiquidityInteraction } from "@/domains/interactions"
import { dollars } from "@/domains/library"
import { ClFarmsPagesComplexBreadcrumbApplication } from "@/domains/navigation/components/complex-breadcrumb-applications/cl-farms-pages-complex-breadcrumb-application"
import { ClFarmsLiquiditySubItem } from "@/domains/navigation/subitems/clfarms-liquidity-subitem"
import { ITokenInputBalance, TokenInputBalance } from "@/domains/tokens/components/token-input-balance"
import { TokenInputValue } from "@/domains/tokens/components/token-input-value"
import { ITokenPercentageButtons, TokenPercentageButtons } from "@/domains/tokens/components/token-percentage-buttons"
import { INSUFFICIENT_BALANCE_ERROR } from "@/features/liquidity/constants"
import { useChainConfig } from "@/hooks/use-chain-config"
import { useClFarm } from "@/hooks/use-cl-farm"
import useEventEmitter from "@/hooks/useEventEmitter"

const BREADCRUMB_ITEMS = [new ClFarmsLiquiditySubItem(), ClFarmsPagesComplexBreadcrumbApplication]

const CLFarmsStake = observer(() => {
  const { address: clFarmAddressParam } = useParams<{ address: string }>()
  const config = useConfig()
  const emitter = useEventEmitter()
  const chainConfig = useChainConfig()
  const scaleApp = useScaleApplication()

  const clFarmAddress = clFarmAddressParam ? (clFarmAddressParam as Address) : undefined

  const clFarm = useClFarm(clFarmAddress)

  const [amount, setAmount] = useState<string>("0")
  const amountBI = clFarm?.parseUnits(amount) ?? 0n

  if (!clFarm) {
    return null
  }

  const hasSufficientBalance = amountBI <= clFarm.userBalance

  const canInteract = amountBI > 0n && hasSufficientBalance

  // #region Stake Action
  const stakeAction = async () => {
    if (!scaleApp.Wallet.Connection.isConnected) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }
    if (!scaleApp.Wallet.Connection.connectedAddress) {
      return emitter.emit("error-not-connected", {
        title: "Not connected",
        description: "In order to interact with this app, you first must connect your wallet.",
        variant: "error",
      })
    }

    const chainId = chainConfig.chainId

    const publicClient = scaleApp.getPublicClient()

    // const wrapperAddress = clsetFromApi?.config.token.address as Address
    // const farmAddress = clsetFromApi?.config.farm as Address

    await stakeClFarmLiquidityInteraction({
      connectedAddress: scaleApp.Wallet.Connection.connectedAddress.toViemAddress(),
      chainId,
      clFarm,
      config,
      publicClient,
      emitter,
      amount: amountBI,
      approvalMode: scaleApp?.Settings.approvalMode,
      onSuccess: () => {
        setAmount("0")
        // we update our CL Farm User Info ,
        scaleApp.ClFarms.Orchestrator.userInfoQueryInstance?.forceExecute()

        // the token should update it's balance
        scaleApp.Tokens.Orchestrator.balanceQuery?.forceExecute()
      },
    })
  }
  // #endregion

  const title = clFarm ? `Stake ${clFarm.displayName} CL Farms Liquidity` : "Stake CL Farms Liquidity"
  const subTitle = "in the gauge for boosted rewards with liquidity automation"
  return (
    <TooltipProvider>
      <PageContainer>
        <MetaData title={title} />
        <PageTitle title="Stake CL Farms Liquidity" subTitle={subTitle} items={BREADCRUMB_ITEMS} />
        <ClFarmsPageLayout
          clFarmAddress={clFarmAddress}
          clFarm={clFarm}
          leftColumn={
            <StakePair
              amount={amount}
              canInteract={canInteract}
              clFarm={clFarm}
              hasSufficientBalance={hasSufficientBalance}
              setAmount={setAmount}
              stakeLiquidityAction={stakeAction}
            />
          }
        />
      </PageContainer>
    </TooltipProvider>
  )
})

export default CLFarmsStake

type StakePairProps = {
  amount: string
  canInteract: boolean
  clFarm: ClFarmStore | null | undefined
  hasSufficientBalance: boolean
  setAmount: (v: string) => void
  stakeLiquidityAction: () => void
}

const StakePair = observer(
  ({ amount, canInteract, clFarm, hasSufficientBalance, setAmount, stakeLiquidityAction }: StakePairProps) => {
    if (!clFarm) {
      return null
    }

    const buttonsToken: ITokenPercentageButtons & ITokenInputBalance = {
      address: clFarm.address,
      symbol: clFarm.symbol ?? "unknown",
      imageURL: clFarm.imageURL ?? "",
      decimals: clFarm.decimals ?? 18,
      displayBalance: clFarm.formatUnits(clFarm.onChainUserBalance ?? 0n),
      calculateValue: (percentage: number): string => {
        return clFarm?.calculateValuePercentage(clFarm.onChainUserBalance, percentage) ?? "0"
      },
    }

    return (
      <Card.Container className="flex w-full min-w-[320px] flex-col gap-6 rounded-lg border border-p400-light bg-p400 p-4 lg:p-6">
        <ShowWhenTrue when={!!clFarm}>
          <ClFarmsSubHeader
            displayName={clFarm?.displayName ?? ""}
            tags={clFarm?.tags ?? []}
            description={clFarm?.description ?? ""}
          />
          <InnerCard hasWarning={!hasSufficientBalance}>
            <div className="flex flex-row items-center justify-between">
              <TokenInputValue
                value={amount}
                token={clFarm}
                onValueChange={(v) => setAmount(v)}
                className="h-fit min-w-[180px] flex-grow p-0"
                placeholder="0"
              />
            </div>

            {/* balances */}
            <div className="flex w-full flex-row items-center justify-between text-xs">
              <ShowWhenTrue when={hasSufficientBalance}>
                <div className="flex items-center gap-1 text-sm text-t200">
                  <DollarIcon size={14} className="text-t200" />
                  <p>~{dollars(Number(clFarm?.calculateValueUSD(Number(amount))))}</p>
                </div>
              </ShowWhenTrue>
              <ShowWhenTrue when={!hasSufficientBalance}>
                <InnerCardWarning warning={INSUFFICIENT_BALANCE_ERROR} />
              </ShowWhenTrue>
              <Tooltip key={"balance"}>
                <TooltipTrigger asChild>
                  <TokenInputBalance token={clFarm} label={<WalletIcon size={14} className="text-t200" />} />
                </TooltipTrigger>
                <TooltipContent>
                  <div>{clFarm?.formatUnits(clFarm?.onChainUserBalance ?? 0n)}</div>
                </TooltipContent>
              </Tooltip>
            </div>

            <TokenPercentageButtons token={buttonsToken} onClick={setAmount} />
          </InnerCard>

          <div className="my-2 flex w-full items-center justify-between">
            <AlmProviderImageLink clFarm={clFarm} />
            <Button disabled={!canInteract} variant={"primary"} onClick={stakeLiquidityAction} size="fit">
              Stake
            </Button>
          </div>
        </ShowWhenTrue>
      </Card.Container>
    )
  },
)
